import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { SetupChecklist } from '../../components/admin/setup/SetupChecklist';
import {
  Plus, Search, AlertTriangle, Package, Layers, Wrench, Boxes,
  Minus, RotateCcw, Pencil, Trash2, History, Loader2, X, Check, FileSpreadsheet, Tag,
} from 'lucide-react';
import { API, formatErr, StatTile, notifyErr } from '../../components/admin/inventory/shared';
import ItemDialog from '../../components/admin/inventory/ItemDialog';
import StockDialog from '../../components/admin/inventory/StockDialog';
import LogsDialog from '../../components/admin/inventory/LogsDialog';
import ImportDialog from '../../components/admin/inventory/ImportDialog';
import { isForSale } from '../../components/admin/inventory/SaleFields';
import { ReviewsDueBanner } from '../../components/admin/ReviewsDueBanner';

// Look-and-feel for the three that ship with Driveline. A workshop's own
// sections come from the API and take the neutral treatment.
const BUILTIN_LOOK = {
  ppf: { icon: Layers, accent: 'from-cyan-500/15 to-cyan-500/5 border-cyan-400/30' },
  sunfilm: { icon: Package, accent: 'from-amber-500/15 to-amber-500/5 border-amber-400/30' },
  bodyshop: { icon: Wrench, accent: 'from-emerald-500/15 to-emerald-500/5 border-emerald-400/30' },
};
const CUSTOM_LOOK = { icon: Boxes, accent: 'from-teal-500/15 to-teal-500/5 border-teal-400/30' };

const InventoryDashboard = () => {
  const { user, authHeader } = useAuth();
  const { tenant } = useTenant();
  const workshopName = tenant?.workshop_name || 'Unisol Global';
  // Stock is readable by the whole workshop; only inventory keepers can move it.
  const canWrite = Boolean(user?.is_master_admin || (user?.permissions || {}).can_manage_inventory);
  const [activeCat, setActiveCat] = useState('ppf');
  const [sections, setSections] = useState([]);
  const [newSection, setNewSection] = useState('');
  const [addingSection, setAddingSection] = useState(false);
  const [renaming, setRenaming] = useState(null); // { id, label }
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [stockDialog, setStockDialog] = useState(null); // { item, mode }
  const [logsDialog, setLogsDialog] = useState(null);
  // Cross-item recent activity feed — the "which material was used on
  // which car" answer the ops team wants at a glance.
  const [recent, setRecent] = useState([]);
  const [recentOpen, setRecentOpen] = useState(true);
  const [recentActionFilter, setRecentActionFilter] = useState('consume');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/inventory`, { headers: authHeader() });
      setItems(data);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  const fetchRecent = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (recentActionFilter && recentActionFilter !== 'all') params.set('action', recentActionFilter);
      const { data } = await axios.get(`${API}/inventory/recent-activity?${params}`, { headers: authHeader() });
      setRecent(data.rows || []);
    } catch (e) { /* non-fatal — panel just stays empty */ }
  }, [authHeader, recentActionFilter]);

  const fetchSections = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/inventory/sections`, { headers: authHeader() });
      setSections(data.sections || []);
    } catch { /* the built-ins still render from the items themselves */ }
  }, [authHeader]);

  const addSection = async () => {
    const label = newSection.trim();
    if (!label) return;
    setAddingSection(true);
    try {
      const { data } = await axios.post(`${API}/inventory/sections`, { label },
        { headers: authHeader() });
      setNewSection('');
      await fetchSections();
      setActiveCat(data.key);
    } catch (e) { notifyErr(e, 'Could not add that section'); } finally { setAddingSection(false); }
  };

  const saveRename = async () => {
    const label = (renaming?.label || '').trim();
    if (!label) return;
    try {
      await axios.patch(`${API}/inventory/sections/${renaming.id}`, { label },
        { headers: authHeader() });
      setRenaming(null);
      await fetchSections();
    } catch (e) { notifyErr(e, 'Could not rename that section'); }
  };

  const removeSection = async (section) => {
    try {
      await axios.delete(`${API}/inventory/sections/${section.id}`, { headers: authHeader() });
      await fetchSections();
      setActiveCat('ppf');
    } catch (e) { notifyErr(e, 'Could not remove that section'); }
  };

  useEffect(() => { fetchSections(); }, [fetchSections]);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  const counts = useMemo(() => {
    const c = { ppf: 0, sunfilm: 0, bodyshop: 0, low: 0, total: 0 };
    items.forEach((i) => {
      c[i.category] = (c[i.category] || 0) + 1;
      c.total += 1;
      if ((i.quantity ?? 0) <= (i.safety_stock ?? 0)) c.low += 1;
    });
    return c;
  }, [items]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((i) => i.category === activeCat)
      .filter((i) => !q || [i.name, i.sku, i.brand, i.hsn_code].some((v) => (v || '').toLowerCase().includes(q)));
  }, [items, activeCat, search]);

  const lowStockItems = useMemo(
    () => items.filter((i) => (i.quantity ?? 0) <= (i.safety_stock ?? 0)),
    [items]
  );

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <SetupChecklist />
        {/* Page header (visible on lg+ since mobile uses the layout top bar) */}
        <div className="hidden lg:flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="inventory-title">Inventory Console</h1>
            <p className="text-sm text-slate-500 mt-0.5">{workshopName} · Internal</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatTile label="Total Items" value={counts.total} testid="stat-total" />
          <StatTile label="PPF" value={counts.ppf} accent="text-cyan-300" testid="stat-ppf" />
          <StatTile label="Sunfilm" value={counts.sunfilm} accent="text-amber-300" testid="stat-sunfilm" />
          <StatTile
            label="Low Stock"
            value={counts.low}
            accent={counts.low > 0 ? 'text-red-400' : 'text-emerald-300'}
            testid="stat-low"
          />
        </div>

        <ReviewsDueBanner />

        {/* Low stock banner */}
        {lowStockItems.length > 0 && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3" data-testid="low-stock-banner">
            <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="font-semibold text-red-700 dark:text-red-200">{lowStockItems.length} item(s) at or below safety stock</div>
              <div className="text-sm text-red-700/80 dark:text-red-200/80 mt-1">
                {lowStockItems.slice(0, 4).map((i) => i.name).join(' · ')}
                {lowStockItems.length > 4 && ` · +${lowStockItems.length - 4} more`}
              </div>
            </div>
          </div>
        )}

        {/* Sections — the three built in, plus whatever this workshop keeps */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {sections.map((cat) => {
            const look = BUILTIN_LOOK[cat.key] || CUSTOM_LOOK;
            const Icon = look.icon;
            const isActive = activeCat === cat.key;
            return (
              <button
                key={cat.key}
                onClick={() => setActiveCat(cat.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all text-sm font-medium ${
                  isActive
                    ? `bg-gradient-to-br ${look.accent} text-white`
                    : 'bg-white/5 border-white/10 text-slate-400 hover:text-slate-200 hover:bg-white/10'
                }`}
                data-testid={`tab-${cat.key}`}
              >
                <Icon size={16} />
                {cat.label}
                <span className="ml-1 text-xs bg-white/10 rounded px-1.5 py-0.5">{counts[cat.key] || 0}</span>
              </button>
            );
          })}

          {canWrite && (
            <div className="inline-flex items-center gap-1.5">
              <input
                value={newSection}
                onChange={(e) => setNewSection(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addSection(); }}
                placeholder="New section…"
                maxLength={60}
                className="w-36 bg-white/5 border border-dashed border-white/20 rounded-lg px-3 py-2.5 text-sm placeholder:text-slate-500 focus:outline-none focus:border-teal-400"
                data-testid="new-section-input"
              />
              <button
                onClick={addSection}
                disabled={addingSection || !newSection.trim()}
                title="Add section"
                className="inline-flex items-center gap-1 text-sm font-medium text-teal-300 hover:text-teal-200 disabled:text-slate-600 px-2 py-2.5"
                data-testid="add-section-btn"
              >
                {addingSection ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}
                Add
              </button>
            </div>
          )}
        </div>

        {/* Renaming or removing the section you are looking at — built-ins are
            Driveline's and stay put. */}
        {canWrite && (() => {
          const current = sections.find((x) => x.key === activeCat);
          if (!current || current.builtin) return null;
          return renaming?.id === current.id ? (
            <div className="flex items-center gap-2 mb-5" data-testid="section-rename-row">
              <input
                value={renaming.label}
                onChange={(e) => setRenaming((r) => ({ ...r, label: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); }}
                maxLength={60}
                className="w-56 bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400"
                data-testid="section-rename-input"
              />
              <button onClick={saveRename} className="text-teal-300 hover:text-teal-200" title="Save" data-testid="section-rename-save">
                <Check size={16} />
              </button>
              <button onClick={() => setRenaming(null)} className="text-slate-400 hover:text-slate-200" title="Cancel">
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 mb-5 text-xs" data-testid="section-actions">
              <button
                onClick={() => setRenaming({ id: current.id, label: current.label })}
                className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-200"
                data-testid="section-rename-btn"
              >
                <Pencil size={12} /> Rename “{current.label}”
              </button>
              <button
                onClick={() => removeSection(current)}
                className="inline-flex items-center gap-1 text-slate-400 hover:text-red-300"
                data-testid="section-delete-btn"
              >
                <Trash2 size={12} /> Remove section
              </button>
              {current.items > 0 && (
                <span className="text-slate-500">
                  Empty it first — it holds {current.items} {current.items === 1 ? 'item' : 'items'}.
                </span>
              )}
            </div>
          );
        })()}

        {/* Search + Add */}
        <div className="flex flex-col md:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, part number, brand or HSN…"
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              data-testid="inventory-search"
            />
          </div>
          {canWrite && (
            <button
              onClick={() => setShowImport(true)}
              className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 font-semibold px-4 py-2.5 rounded-lg transition-colors"
              data-testid="import-items-button"
            >
              <FileSpreadsheet size={16} /> Import
            </button>
          )}
          {canWrite && (
            <button
              onClick={() => { setEditingItem(null); setShowItemDialog(true); }}
              className="inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold px-4 py-2.5 rounded-lg transition"
              data-testid="add-item-button"
            >
              <Plus size={16} /> Add Item
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="inventory-error">
            {error}
          </div>
        )}

        {/* Real-time consumption feed — "what got used on which car" */}
        <div className="mb-6 bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid="recent-consumption-panel">
          <button
            onClick={() => setRecentOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition"
            data-testid="recent-toggle"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-teal-500/20 text-teal-300 flex items-center justify-center">
                <History size={16} />
              </div>
              <div className="text-left">
                <div className="text-sm font-bold text-slate-100">Recent consumption</div>
                <div className="text-[11px] text-slate-400">
                  Latest {recentActionFilter === 'consume' ? 'material used' : recentActionFilter === 'restock' ? 'restocks' : 'stock changes'} across all items.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={recentActionFilter}
                onChange={(e) => { e.stopPropagation(); setRecentActionFilter(e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-900 border border-white/10 text-xs text-slate-200 rounded px-2 py-1"
                data-testid="recent-filter"
              >
                <option value="consume">Consumed</option>
                <option value="restock">Restocked</option>
                <option value="all">All</option>
              </select>
              <span className="text-slate-400 text-xs">{recentOpen ? '▲' : '▼'}</span>
            </div>
          </button>
          {recentOpen && (
            <div className="border-t border-white/10 max-h-72 overflow-y-auto">
              {recent.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-slate-500">No activity yet.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-900/60 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left">When</th>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-left">Car</th>
                      <th className="px-3 py-2 text-left">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {recent.map((r) => {
                      const dt = new Date(r.transaction_date || r.created_at);
                      const qty = Math.abs(Number(r.quantity_change || 0));
                      return (
                        <tr key={r.id} data-testid={`recent-row-${r.id}`}>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap tabular-nums">
                            <div>{dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                            <div className="text-[10px] text-slate-500">{dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                          </td>
                          <td className="px-3 py-2 text-slate-200 max-w-[220px]">
                            <div className="font-semibold truncate">{r.item_name}</div>
                            {r.item_sku && <div className="text-[10px] text-slate-500 font-mono">{r.item_sku}</div>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold">
                            <span className={r.action === 'consume' ? 'text-orange-300' : 'text-emerald-300'}>
                              {r.action === 'consume' ? '−' : '+'}{qty} <span className="text-slate-500 font-normal">{r.item_unit}</span>
                            </span>
                          </td>
                          <td className="px-3 py-2 text-cyan-300 whitespace-nowrap">{r.car_model || <span className="text-slate-600">—</span>}</td>
                          <td className="px-3 py-2 text-slate-400 max-w-[160px] truncate" title={r.user_email}>{r.user_email}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Items Table */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 border-b border-white/10">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3">Item</th>
                  <th className="px-4 py-3">SKU</th>
                  <th className="px-4 py-3 text-right">Quantity</th>
                  <th className="px-4 py-3 text-right">Reorder at</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400" data-testid="inventory-loading">
                    <Loader2 className="animate-spin inline mr-2" size={16} /> Loading…
                  </td></tr>
                )}
                {!loading && filteredItems.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400" data-testid="inventory-empty">
                    No items in this category.
                  </td></tr>
                )}
                {!loading && filteredItems.map((item) => {
                  const isLow = (item.quantity ?? 0) <= (item.safety_stock ?? 0);
                  const isCritical = (item.quantity ?? 0) === 0;
                  return (
                    <tr key={item.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`item-row-${item.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-100 flex items-center gap-2 flex-wrap">
                          {item.name}
                          {isForSale(item.usage) && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-teal-500/15 text-teal-200 border border-teal-400/30 rounded px-1.5 py-0.5" data-testid={`sale-badge-${item.id}`}>
                              <Tag size={10} /> {item.usage === 'both' ? 'Use + sale' : 'For sale'}
                              {item.selling_price ? ` · ₹${Number(item.selling_price).toLocaleString('en-IN')}` : ''}
                            </span>
                          )}
                        </div>
                        {(item.brand || item.hsn_code) && (
                          <div className="text-xs text-slate-400 mt-0.5">{[item.brand, item.hsn_code && `HSN ${item.hsn_code}`].filter(Boolean).join(' · ')}</div>
                        )}
                        {item.location && <div className="text-xs text-slate-500 mt-0.5">{item.location}{item.supplier ? ` · ${item.supplier}` : ''}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-400 font-mono text-xs">{item.sku || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${isCritical ? 'text-red-400' : isLow ? 'text-amber-300' : 'text-slate-100'}`} data-testid={`item-qty-${item.id}`}>
                          {item.quantity}
                        </span>
                        <span className="text-slate-500 text-xs ml-1">{item.unit}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-400">{item.safety_stock} {item.unit}</td>
                      <td className="px-4 py-3">
                        {isCritical ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-red-500/15 text-red-300 border border-red-500/30 rounded px-2 py-0.5" data-testid={`status-${item.id}`}>Out of stock</span>
                        ) : isLow ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded px-2 py-0.5" data-testid={`status-${item.id}`}>
                            <AlertTriangle size={11} /> Low
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded px-2 py-0.5" data-testid={`status-${item.id}`}>OK</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1.5">
                          {canWrite && (
                            <>
                              <button
                                onClick={() => setStockDialog({ item, mode: 'consume' })}
                                className="inline-flex items-center gap-1 text-xs bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/30 text-orange-300 px-2.5 py-1.5 rounded transition"
                                data-testid={`consume-btn-${item.id}`}
                                title="Record consumption"
                              >
                                <Minus size={12} /> Consume
                              </button>
                              <button
                                onClick={() => setStockDialog({ item, mode: 'restock' })}
                                className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 px-2.5 py-1.5 rounded transition"
                                data-testid={`restock-btn-${item.id}`}
                                title="Restock"
                              >
                                <RotateCcw size={12} /> Restock
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setLogsDialog(item)}
                            className="inline-flex items-center justify-center w-8 h-8 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 hover:text-slate-200 rounded transition"
                            data-testid={`logs-btn-${item.id}`}
                            title="View logs"
                          >
                            <History size={13} />
                          </button>
                          {canWrite && (
                            <button
                              onClick={() => { setEditingItem(item); setShowItemDialog(true); }}
                              className="inline-flex items-center justify-center w-8 h-8 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 hover:text-slate-200 rounded transition"
                              data-testid={`edit-btn-${item.id}`}
                              title="Edit item"
                            >
                              <Pencil size={13} />
                            </button>
                          )}
                          {canWrite && user?.role === 'admin' && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
                                try {
                                  await axios.delete(`${API}/inventory/${item.id}`, { headers: authHeader() });
                                  fetchAll();
                                  fetchSections();
                                } catch (e) {
                                  notifyErr(e);
                                }
                              }}
                              className="inline-flex items-center justify-center w-8 h-8 bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-slate-400 hover:text-red-300 rounded transition"
                              data-testid={`delete-btn-${item.id}`}
                              title="Delete item"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {showItemDialog && (
        <ItemDialog
          item={editingItem}
          activeCat={activeCat}
          sections={sections}
          onClose={() => { setShowItemDialog(false); setEditingItem(null); }}
          onSaved={() => { setShowItemDialog(false); setEditingItem(null); fetchAll(); fetchSections(); }}
          authHeader={authHeader}
        />
      )}

      {showImport && (
        <ImportDialog
          onClose={() => setShowImport(false)}
          onDone={() => { fetchAll(); fetchRecent(); }}
          authHeader={authHeader}
        />
      )}

      {stockDialog && (
        <StockDialog
          item={stockDialog.item}
          mode={stockDialog.mode}
          onClose={() => setStockDialog(null)}
          onSaved={() => { setStockDialog(null); fetchAll(); fetchRecent(); }}
          authHeader={authHeader}
        />
      )}

      {logsDialog && (
        <LogsDialog
          item={logsDialog}
          onClose={() => setLogsDialog(null)}
          authHeader={authHeader}
          onItemChanged={() => { fetchAll(); fetchRecent(); }}
        />
      )}
    </div>
  );
};

export default InventoryDashboard;
